"use client";

import React, { useState, useCallback, useEffect } from "react";
import { useReactiveSearchParams } from "@/lib/Hooks/useReactiveSearchParams";
import styled from "styled-components";
import { IconButtonsGroup } from "@lmn/design-system";
import IconCoffee from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-linear/IconCoffee.js";
import IconHalfBoardMeal from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-linear/IconHalfBoardMeal.js";
import IconFullBoardMeal from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-linear/IconFullBoardMeal.js";
import IconAllInclusiveMeal from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-linear/IconAllInclusiveMeal.js";
import IconCircleInfo from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-linear/IconCircleInfo.js";
import { MealTypeIds } from "@/lib/Types/searchForm";
import initialState from "@/lib/StaticData/searchFormData";
import { useFilterState } from "@/lib/Providers/FilterStateProvider/FilterStateProvider";
import ResponsiveTooltip from "@/components/UI/Tooltip/ResponsiveTooltip";

const FilterWrapper = styled.div`
  margin-top: 1rem;
  background-color: ${(props) =>
    props.theme.kallisto.color.background.static.backgroundPage};
  padding: 1rem 0;
  border-radius: 8px;
`;

const OptionTitle = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.2rem;
`;

const mealTypeIcons: { [key in MealTypeIds]: React.ReactNode } = {
  2: <IconCoffee size={24} />,
  3: <IconHalfBoardMeal size={24} />,
  4: <IconFullBoardMeal size={24} />,
  5: <IconAllInclusiveMeal size={24} />,
};

const mealTypeTooltipContent: { [key in MealTypeIds]: string } = {
  2: "",
  3: "Im Reisepreis sind Frühstück und Abendessen enthalten. In seltenen Fällen wird das Mittagessen anstelle des Abendessens angeboten. Die Getränke beim Abendessen sind normalerweise nicht im Preis inbegriffen.",
  4: "Der Reisepreis beinhaltet drei Mahlzeiten - Frühstück, Mittagessen und Abendessen. In der Regel sind Getränke während des Abendessens nicht enthalten.",
  5: "Im Reisepreis ist eine umfassende Verpflegung am Urlaubsort enthalten. Zusätzlich zu drei großzügigen Mahlzeiten pro Tag werden in der Regel auch kostenfreie kleine Snacks und Getränke angeboten. Die spezifischen Leistungen im All-Inclusive-Paket können je nach Reiseveranstalter variieren.",
};

interface MealTypeFilterProps {
  delayedUpdate?: boolean;
}

const MealTypeFilter: React.FC<MealTypeFilterProps> = ({
  delayedUpdate = false,
}) => {
  const searchParams = useReactiveSearchParams();
  const { tempFilters, updateFilter, updateTempFilter } = useFilterState();
  const mealType = initialState.mealType;

  const [selectedMealType, setSelectedMealType] = useState<string | null>(
    () => {
      return delayedUpdate
        ? tempFilters.get("mealType") ?? null
        : searchParams.get("mealType") ?? null;
    }
  );

  useEffect(() => {
    setSelectedMealType(searchParams.get("mealType") ?? null);
  }, [searchParams]);

  const options = mealType.allIds
    .filter((id): id is MealTypeIds => id !== false)
    .map((id) => ({
      id: id.toString(),
      title: (
        <OptionTitle>
          {mealType.byId[id].label}
          {mealTypeTooltipContent[id] && (
            <ResponsiveTooltip
              content={mealTypeTooltipContent[id]}
              trigger={<IconCircleInfo size={14} />}
            />
          )}
        </OptionTitle>
      ),
      icon: mealTypeIcons[id],
    }));

  const handleMealTypeChange = useCallback(
    (id: string) => {
      const newMealType = id === selectedMealType ? null : id;
      setSelectedMealType(newMealType);

      if (delayedUpdate) {
        // Just update temp filters, will be committed on dialog confirmation
        updateTempFilter("mealType", newMealType);
      } else {
        // Immediate URL update
        updateFilter("mealType", newMealType);
      }
    },
    [updateFilter, updateTempFilter, delayedUpdate, selectedMealType]
  );

  return (
    <FilterWrapper data-testid="mealTypeFilter">
      <IconButtonsGroup
        maxColumns={2}
        // @ts-expect-error: change this in design system so that it accept React Components
        options={options}
        selected={selectedMealType ? [selectedMealType] : []}
        onToggle={handleMealTypeChange}
        aria-label="Wählen Sie ein oder mehrere Mahlzeiten"
        aria-describedby="meal-type-description"
      />
      <span id="meal-type-description" className="sr-only">
        Wählen Sie ein oder mehrere Mahlzeiten aus der Liste aus. Verwenden Sie
        die Pfeiltasten, um durch die Optionen zu navigieren, die Leertaste zum
        Auswählen und Enter zum Bestätigen.
      </span>
    </FilterWrapper>
  );
};

export default MealTypeFilter;
